/* FLOWS POPUPS ON THE SITE.

   A marketing journey in FLOWS (flows.juce.co.za) can queue a popup for a
   person's NEXT visit: "your 10% is still waiting", "welcome back, here is
   what is new". This file asks FLOWS, once per page, whether the person in
   front of it has one waiting, and if so shows it the way the journey said -
   as they arrive, after a few seconds, part-way down the page, or as they
   leave - inside a shadow root, so nothing on the page can restyle it and it
   cannot restyle the page.

   The rules it keeps:

     1. NOTHING WITHOUT PERSONALISATION CONSENT. A popup made for somebody is
        personalisation by definition, so the question is not even asked until
        juce-consent.js says the visitor granted it. A "no" means this file
        does nothing at all, and a withdrawal mid-page closes an open popup.
     2. NEVER AT CHECKOUT, never in the theme editor, never on an unpublished
        theme (unless ?flows_preview=1 is on the address, for checking one).
     3. AT MOST ONE A PAGE and never the same one twice in a visit; FLOWS keeps
        the count across visits.
     4. EVERY FAILURE IS SILENT. A slow or missing FLOWS costs the page nothing:
        the request waits for the page to be idle and gives up after four
        seconds.

   What it sends: the Loom visitor id (which the consent has already allowed
   the tracker to keep), the page family's path, and - when somebody arrived
   from one of our messages - the visit token on the address (?jfm=). The
   token is taken off the address as soon as this file runs, whatever the
   consent, so a shared link does not carry it; it only says "came from
   message N", cannot unsubscribe anybody, and lapses in two days. */

const ENDPOINT = ((window.theme && window.theme.flows && window.theme.flows.endpoint) || 'https://flows.juce.co.za').replace(/\/+$/, '');
const SHOWN_KEY = 'juce:flows:shown';

function consentGiven() {
  try { return Boolean(window.juceConsent && window.juceConsent.granted('personalisation')); } catch (err) { return false; }
}

function allowedHere() {
  try {
    if (window.Shopify && window.Shopify.designMode) return false;
    const path = location.pathname.toLowerCase();
    if (path.indexOf('/checkout') === 0 || path.indexOf('/checkouts') === 0) return false;
    const preview = /[?&]flows_preview=1\b/.test(location.search);
    const role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
    if (role && role !== 'main' && !preview) return false;
    return true;
  } catch (err) { return false; }
}

function visitorId() {
  try {
    const s = JSON.parse(localStorage.getItem('juce:loom:v1') || 'null');
    return s && typeof s.aid === 'string' ? s.aid : '';
  } catch (err) { return ''; }
}

/* The message token from a link in one of our emails or WhatsApps, taken
   off the address once it has been read. */
function takeMessageToken() {
  try {
    const u = new URL(location.href);
    const t = u.searchParams.get('jfm') || '';
    if (t) {
      u.searchParams.delete('jfm');
      history.replaceState(history.state, '', u.pathname + (u.search ? u.search : '') + u.hash);
    }
    return /^[A-Za-z0-9_-]{8,40}$/.test(t) ? t : '';
  } catch (err) { return ''; }
}

function shownThisVisit(token) {
  try { return (sessionStorage.getItem(SHOWN_KEY) || '').split(',').indexOf(token) !== -1; } catch (err) { return false; }
}
function markShown(token) {
  try {
    const list = (sessionStorage.getItem(SHOWN_KEY) || '').split(',').filter(Boolean);
    list.push(token);
    sessionStorage.setItem(SHOWN_KEY, list.slice(-20).join(','));
  } catch (err) { /* private mode */ }
}

function report(token, kind) {
  const body = JSON.stringify({ token, kind });
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT + '/p/event', new Blob([body], { type: 'text/plain' }))) return;
  } catch (err) { /* fall through */ }
  try { fetch(ENDPOINT + '/p/event', { method: 'POST', body, keepalive: true, credentials: 'include', headers: { 'Content-Type': 'text/plain' } }); } catch (err) { /* nothing */ }
}

function loomTrack(kind, token) {
  try { if (window.loom && typeof window.loom.track === 'function') window.loom.track('flows.popup', { kind, token }); } catch (err) { /* nothing */ }
}

let open = null;

function show(p) {
  /* Asked again at the moment it would open: consent withdrawn during the
     delay (or the scroll, or before the exit) means it never opens. */
  if (open || shownThisVisit(p.token) || !consentGiven()) return;
  const layout = p.layout || {};
  const host = document.createElement('div');
  host.setAttribute('data-juce-flows', '');
  host.style.cssText = 'position:relative;z-index:2147483000';
  const root = host.attachShadow({ mode: 'open' });
  const pos = layout.type === 'corner' ? 'jf-pos-corner jf-right' : layout.type === 'bar' ? 'jf-pos-bar' : '';
  root.innerHTML = '<style>' + String(p.css || '') + '</style>'
    + '<div class="jf-scrim ' + pos + '" part="scrim">' + String(p.html || '') + '</div>';
  const scrim = root.querySelector('.jf-scrim');
  const card = root.querySelector('.jf-card');
  if (!scrim || !card) return;
  const modal = !layout.type || layout.type === 'modal' || layout.type === 'sheet';
  card.setAttribute('role', 'dialog');
  if (modal) card.setAttribute('aria-modal', 'true');
  const heading = card.querySelector('.jf-h');
  card.setAttribute('aria-label', heading ? heading.textContent.trim().slice(0, 120) : 'A message from JUCE');
  const x = document.createElement('button');
  x.className = 'jf-x';
  x.type = 'button';
  x.setAttribute('aria-label', 'Close');
  x.textContent = String.fromCharCode(215);
  card.insertBefore(x, card.firstChild);

  const previous = document.activeElement;
  let acted = false;
  const close = (why) => {
    if (!open) return;
    document.removeEventListener('keydown', onKey, true);
    host.remove();
    open = null;
    if (why && !acted) { report(p.token, 'closed'); loomTrack('closed', p.token); }
    try { if (previous && previous.focus) previous.focus(); } catch (err) { /* gone */ }
  };
  const onKey = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); close('escape'); }
    if (e.key === 'Tab' && modal) {
      /* Keep the keyboard inside the popup while it is open. */
      const f = Array.prototype.slice.call(card.querySelectorAll('a[href], button'));
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      const active = root.activeElement;
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    }
  };
  x.addEventListener('click', () => close('x'));
  if (modal) scrim.addEventListener('click', (e) => { if (e.target === scrim) close('backdrop'); });
  card.addEventListener('click', (e) => {
    const t = e.target && e.target.closest ? e.target.closest('[data-jf-act], [data-jf-copy]') : null;
    if (!t) return;
    acted = true;
    report(p.token, 'clicked');
    loomTrack('clicked', p.token);
    const code = t.getAttribute('data-jf-copy');
    if (code) {
      e.preventDefault();
      try {
        navigator.clipboard.writeText(code).then(() => { t.textContent = 'Copied'; setTimeout(() => { t.textContent = code; }, 1800); });
      } catch (err) { /* select it instead */ }
    }
  });
  document.addEventListener('keydown', onKey, true);
  document.body.appendChild(host);
  open = { close };
  markShown(p.token);
  report(p.token, 'shown');
  loomTrack('shown', p.token);
  try { x.focus({ preventScroll: true }); } catch (err) { /* old browser */ }
}

function arm(p) {
  const rules = p.rules || {};
  const go = () => show(p);
  if (rules.show === 'landing') { go(); return; }
  if (rules.show === 'scroll') {
    const want = Math.min(95, Math.max(5, Number(rules.scrollPercent) || 40)) / 100;
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - innerHeight;
      if (h <= 0 || scrollY / h >= want) { removeEventListener('scroll', onScroll); go(); }
    };
    addEventListener('scroll', onScroll, { passive: true });
    return;
  }
  if (rules.show === 'exit') {
    /* A mouse heading for the tabs on a desktop; a phone has no such moment,
       so it gets the popup after a longer settle instead. */
    if (matchMedia('(hover: hover) and (pointer: fine)').matches) {
      const onOut = (e) => { if (!e.relatedTarget && e.clientY <= 0) { document.removeEventListener('mouseout', onOut); go(); } };
      document.addEventListener('mouseout', onOut);
    } else {
      setTimeout(go, 20000);
    }
    return;
  }
  setTimeout(go, Math.min(120, Math.max(0, Number(rules.delaySeconds) || 4)) * 1000);
}

/* Taken off the address the moment this file runs, consent or not: a
   visitor who says no to personalisation still should not carry the token on
   a link they copy and share. It is only SENT once consent is given. */
let arrivedWith = '';

async function ask() {
  const token = arrivedWith;
  const qs = new URLSearchParams({ path: location.pathname.slice(0, 200) });
  const aid = visitorId();
  if (aid) qs.set('aid', aid);
  if (token) qs.set('jfm', token);
  const ctl = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = ctl ? setTimeout(() => ctl.abort(), 4000) : null;
  try {
    const res = await fetch(ENDPOINT + '/p/next?' + qs.toString(), { credentials: 'include', signal: ctl ? ctl.signal : undefined });
    if (!res.ok) return;
    const data = await res.json();
    if (data && data.popup && data.popup.token && data.popup.html) arm(data.popup);
  } catch (err) { /* rule 4 */ } finally { if (timer) clearTimeout(timer); }
}

/* QR SCANS (2026-09-14). A scan lands here wearing ?jqr=<token> (see the
   ?qr= reader at the top of layout/theme.liquid). The token is taken off the
   address at once, whatever the consent, so a shared link does not carry it,
   and the page CLAIMS it: first just "the scan landed, on this path", and
   again with LOOM's browser id once the visitor's record grants analytics
   and LOOM has one - which is what lets an order days later be credited to
   the poster. The token belongs to the scan, not to anybody; it cannot
   unsubscribe, sign in or change anything, and FLOWS lets a claim fill a gap
   but never move a scan to another browser. Kept for the tab only. */
const QR_KEY = 'juce:qr:claim';

function takeScanToken() {
  try {
    const u = new URL(location.href);
    const t = u.searchParams.get('jqr') || '';
    if (t) {
      u.searchParams.delete('jqr');
      history.replaceState(history.state, '', u.pathname + (u.search ? u.search : '') + u.hash);
      if (/^[A-Za-z0-9_-]{12,32}$/.test(t)) sessionStorage.setItem(QR_KEY, JSON.stringify({ t, sent: 0 }));
    }
  } catch (err) { /* private mode */ }
}

function analyticsGiven() {
  try { return Boolean(window.juceConsent && window.juceConsent.granted('analytics')); } catch (err) { return false; }
}

function grantLetters() {
  try {
    const m = document.cookie.match(/(?:^|;\s*)juce_consent=([^;]+)/);
    const parts = m ? decodeURIComponent(m[1]).split('.') : [];
    return parts.length >= 4 && parts[0] === '1' ? parts[3] : '';
  } catch (err) { return ''; }
}

let qrTries = 0;
function claimScan() {
  let st = null;
  try { st = JSON.parse(sessionStorage.getItem(QR_KEY) || 'null'); } catch (err) { return; }
  if (!st || !st.t || st.sent >= 2) return;
  const aid = analyticsGiven() ? visitorId() : '';
  /* The first claim goes as soon as the page runs; the second only carries
     news - a browser id - so it waits until there is one to send. */
  if (st.sent === 1 && !aid) {
    if (analyticsGiven() && qrTries++ < 6) setTimeout(claimScan, 2000);
    return;
  }
  const body = JSON.stringify({ t: st.t, path: location.pathname.slice(0, 200), c: grantLetters(), aid: aid || undefined });
  let sent = false;
  try { sent = Boolean(navigator.sendBeacon && navigator.sendBeacon(ENDPOINT + '/q/claim', new Blob([body], { type: 'text/plain' }))); } catch (err) { sent = false; }
  if (!sent) { try { fetch(ENDPOINT + '/q/claim', { method: 'POST', body, keepalive: true, credentials: 'include', headers: { 'Content-Type': 'text/plain' } }); } catch (err) { /* nothing */ } }
  st.sent = aid ? 2 : 1;
  try { sessionStorage.setItem(QR_KEY, JSON.stringify(st)); } catch (err) { /* nothing */ }
  if (st.sent === 1 && analyticsGiven()) setTimeout(claimScan, 2000);
}

function startQr() {
  try { if (window.Shopify && window.Shopify.designMode) return; } catch (err) { return; }
  takeScanToken();
  claimScan();
  document.addEventListener('juce:consent', () => { qrTries = 0; claimScan(); });
}

try { startQr(); } catch (err) { /* rule 4 */ }

function start() {
  arrivedWith = takeMessageToken();
  if (!allowedHere()) return;
  let asked = false;
  const maybe = () => {
    if (asked || !consentGiven()) return;
    asked = true;
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 1200));
    idle(() => { ask(); });
  };
  /* The consent script may not have decided yet: wait for it, and for the
     visitor's answer to the banner if it is still up. */
  try { if (window.juceConsent && window.juceConsent.ready) window.juceConsent.ready.then(maybe, () => {}); } catch (err) { /* nothing */ }
  document.addEventListener('juce:consent', () => {
    if (!consentGiven() && open) open.close();
    maybe();
  });
  maybe();
}

try { start(); } catch (err) { /* rule 4 */ }
